<template>
  <div>
      <modal ref='deleteRecordMessageModal'>
        <template #header>
          <h5 class="modal-title mt-0">{{ $t('record_message.delete_message') }}</h5>
        </template>

        <template #body>
          <form @submit.prevent='deleteRecordMessage'>
            <div class='modal-body'>
              <p>{{ $t('record_message.are_you_sure') }}</p>
            </div>

            <div class='modal-footer'>
              <input @click.prevent='$refs.deleteRecordMessageModal.close' type='submit' :value="$t('notes.close')" class="btn btn-outline-primary" :disabled="isProcessing" />
              <button type='submit' class="btn btn-danger">
                {{ $t('notes.delete') }}
                <i class="fas fa-circle-notch fa-spin" v-show="isProcessing"></i>
              </button>
            </div>
          </form>
        </template>
      </modal>

      <div class="set-data communication-data" v-if="modalIsVisible">
        <message-fake-loading :loading="loadingMessages" :fakes="4" />
        <i class="fas fa-times" @click="hideIt"></i>
        <div v-show="!loadingMessages && showing !== 'newRecordMessage'" class="messages">
          <ul class="request_message">
            <li v-for='msg in messages' :key='msg.id'>
              <div class="main-message">
                <div class="sender-info">
                  <user-image :user='msg.from_user' />
                  <span>{{ msg.from_user.full_name }}</span>
                </div>
                <div class="msg-details-container">
                  <p class="date">
                    {{ formatTime(msg.created_at) }}
                    {{ formatDate(msg.created_at) }}
                    <a v-if="unreadStats(msg.id) > 0" @click.prevent="updateSeenMessages(msg.id)" class="unread-messages-here">
                      <i class="fas fa-envelope"></i>
                      <span v-show="unreadStats(msg.id) === 1">
                            {{ $t('record_message.unread_new_message_count', {count: unreadStats(msg.id)}) }}
                      </span>
                      <span v-show="unreadStats(msg.id) > 1">
                            {{ $t('record_message.unread_new_messages_count', {count: unreadStats(msg.id)}) }}
                      </span>
                    </a>
                  </p>
                  <p v-html="msg.body" class="text"></p>
                  <p>
                    {{ $t('record_message.sent_to_label')}}: {{ sentTo(msg) }}
                  </p>
                </div>
                <div class="msg-actions-container">
                  <a
                    href="#" @click.prevent="showReplyForm(msg, {})" :data-tooltip="$t('record_message.reply_all')"
                    class="own-tooltip own-tooltip-left">
                    <i class="fas fa-reply-all" />
                  </a>
                  <a href="#" @click.prevent="showForwardRecordMessage(msg)" :data-tooltip="$t('record_message.forward')"
                     class="own-tooltip own-tooltip-left m-r-10">
                    <i class="fas fa-arrow-right"></i>
                  </a>
                  <a href="#" @click.prevent='openDeleteRecordMessageModal(msg, $refs.deleteRecordMessageModal)' :data-tooltip="$t('record_message.delete_message')"
                     class="own-tooltip own-tooltip-left">
                    <i class="fas fa-trash-alt" />
                  </a>
                </div>
              </div>
              <div class="replies-container">
                <a href="#" v-show="msg.replies.length > 0 && showing !== `${msg.id}-replies`" @click.prevent="showReplies(msg, true)" class="reply-collapse">
                  {{ $t('record_message.view_all_replies', { count: msg.replies.length } ) }}
                  <i class="fas fa-chevron-down"></i>
                </a>
                <a href="#" v-show="showing===`${msg.id}-replies`" @click.present="showReplies(msg, false)" class="reply-collapse">
                  {{ $t('record_message.collapse') }}
                  <i class="fas fa-chevron-up"></i>
                </a>
                <transition-expand>
                  <ul class="replies" v-if="showing===`${msg.id}-replies`">
                    <li v-for='reply in orderedReplies(msg.replies)' :key='reply.id'>
                      <div class="sender-info">
                        <div v-if="reply.from_user">
                          <user-image :user='reply.from_user'/>
                          <span>{{ reply.from_user.full_name }}</span>
                        </div>
                        <div v-else-if="reply.from_contact">
                          <div class="contact-image-wrapper rounded-circle">
                            <span>
                              {{ reply.from_contact.name ? reply.from_contact.name.substring(0, 1) : '' }}
                            </span>
                          </div>
                          <span>{{ reply.from_contact.name }}</span>
                        </div>
                      </div>
                      <div class="msg-details-container">
                        <p class="date">
                          {{ formatTime(reply.created_at) }}
                          {{ formatDate(reply.created_at) }}
                        </p>
                        <p class="via">
                          {{ $t('record_message.sent_via') }}:
                          {{ repliedVia(reply.sent_via) }}
                        </p>
                        <p v-show="reply.forwarded">--- {{ $t('record_message.forwarded_message') }} ---<p>
                        <p v-html="reply.body" class="text"></p>
                        <p>
                          {{ $t('record_message.sent_to_label')}}: {{ replySentTo(reply) }}
                        </p>
                        <message-reactions :message="reply" messageType='reply' :users="users" @success="loadRecordMessages(false)" />
                      </div>
                      <div class="msg-actions-container">
                        <a href="#" @click.prevent="showReplyForm(msg, reply)">
                          <i class="fas fa-reply"></i>
                          {{ $t('record_message.reply') }}
                        </a>
                      </div>
                    </li>
                  </ul>
                  <reply-record-message
                    v-if="showing === `${msg.id}-replyRecordMessage`"
                    :message="selectedMessage"
                    :replyMessage="selectedReplyMessage"
                    :addressBook="addressBook"
                    :resourceType='resource'
                    @success="closeRecordMessage()" />
                  <forward-record-message
                    v-if="showing === `${msg.id}-forwardRecordMessage`"
                    :message="selectedMessage"
                    :addressBook="addressBook"
                    :resourceType='resource'
                    @success="closeRecordMessage()" />
                </transition-expand>
              </div>
            </li>
          </ul>
          <button v-if="showing && showing.match(/messages|replies/)"
                  @click.prevent="openNewRecordMessage()"
                  class="btn btn-outline-primary">
            <i class="fas fa-paper-plane"></i>
            {{$t('record_message.send_new_message')}}
          </button>
        </div>
        <form ref="createRecordMessageForm" @submit.prevent='createRecordMessage' v-show="showing==='newRecordMessage'" class="send-message-now">
          <label for="record_message_sent_contacts">{{ $t('record_message.send_to_label')}}:</label>
          <multi-select :getter='getAddressBook' v-model='selectedAddressBook' />
          <label for="record_message_body">{{ $t('record_message.message_label')}}:</label>
          <input type="hidden" id='record_message_body' name="record_message[body]" v-model="recordMessageBody" />
          <trix-editor input="record_message_body" class="trix-body tinymce"></trix-editor>
          <button type="submit" class="btn btn-outline-primary submit-note float-right" :disabled="recordMessageBody == '' || selectedAddressBook.length === 0 || isProcessing">
            {{$t('record_show.send')}}
            <i class="fas fa-circle-notch fa-spin" v-show="isProcessing"></i>
          </button>
          <button @click.prevent="((messages.length>0) ? showing='messages' : showing=null)" class="btn float-right btn-outline-danger m-r-10">
            {{$t('record_show.cancel')}}
          </button>
        </form>
      </div>
  </div>
</template>

<script>
  import _modal from '../../components/_modal'
  import _user_image from '../_user_image'
  import _multi_select from '../shared/_infinite_multi_select'
  import _reply_record_message from '../communications/_reply_record_message'
  import _forward_record_message from '../communications/_forward_record_message_form'
  import _fake_loading from '../communications/_record_message_fake_loading'
  import _message_reactions from '../communications/_message_reactions'
  import TransitionExpand from '../shared/transition_expand'
  import 'trix'
  import 'trix/dist/trix.css'

  export default {
    components: {
      modal: _modal,
      'user-image': _user_image,
      'multi-select': _multi_select,
      'reply-record-message': _reply_record_message,
      'forward-record-message': _forward_record_message,
      'message-fake-loading': _fake_loading,
      'message-reactions': _message_reactions,
      'transition-expand': TransitionExpand
    },
    props: {
      currentRecord: {
        required: true
      },
      hash: {
          type: String
      },
      resourceType: {
          type: String
      }
    },
    data() {
      return {
        modalRecordMessage: {},
        showing: null,
        isProcessing: false,
        recordMessageBody: '',
        selectedMessage: {},
        selectedReplyMessage: {},
        selectedAddressBook: [],
        loadingMessages: false
      }
    },
    mounted(){
      document.addEventListener("trix-change", this.setTextToTrix)
    },
    beforeDestroy() {
      document.removeEventListener("trix-change", this.setTextToTrix)
    },
    watch: {
      record() {
        if(this.record.id !== undefined)
          this.autoloadModal()
      },
    },
    computed: {
      record() {
        return this.$props.currentRecord || {}
      },
      messages() {
        return this.state.recordMessages || []
      },
      recordMessageStats() {
        return this.stats.recordMessageStats || []
      },
      repliesStats(){
        return this.recordMessageStats.replies || []
      },
      newMessageStats(){
        return this.recordMessageStats.new_messages || []
      },
      addressBook() {
        return (this.lists.addressBook || []).map( (item) => { return { id: item.id, name: item.name } }, [])
      },
      messageIsSelected(){
        return (Object.keys(this.selectedMessage).length > 0)
      },
      resource(){
        return this.$props.resourceType || ''
      },
      modalIsVisible(){
        return this.showing &&
               this.showing.match(/messages|replies|newRecordMessage|replyRecordMessage|forwardRecordMessage/) &&
               this.toggles('file_communication')
      },
      users() {
        return this.lists.users || []
      }
    },
    methods: {
      getAddressBook(params) {
        return this.listStore.getAddressBook(params)
      },
      autoloadModal(){
        let hash = ((this.$props.hash !== undefined) ? this.$props.hash.substring(1) : '')
        if(hash==='messages')
          this.openMessages()
      },
      setTextToTrix() {
        this.recordMessageBody = document.getElementById("record_message_body").value
      },
      orderedReplies(replies){
        //the oldest to the newest
        return replies.slice(0).sort((a, b) =>
                 new Date(a.created_at) - new Date(b.created_at)
               )
      },
      loadRecordMessages(show_loading=true){
        this.loadingMessages = show_loading
        if(this.loadingMessages === true)
          this.showing = 'messages'
        return this.store.getRecordMessages(this.record.id).finally(() => {
          this.listStore.getUsers()
          this.updateStatsCounters()
          this.loadingMessages = false
          if(this.messages.length === 0){
            this.showing = 'newRecordMessage'
            this.sendMetricComponent('send_new_message_form')
          }else{
            this.sendMetricComponent('open_messages_modal')
          }
        }).catch(() => {
          this.loadingMessages = false
        })

      },
      repliedVia(via){
        switch (via) {
          case 'app':
            return this.$t('record_message.replied_from_app')
          case 'email':
            return this.$t('record_message.replied_from_email')
          case 'public_page':
            return this.$t('record_message.replied_from_public_page')
        }
      },
      openDeleteRecordMessageModal(recordMessage, modal) {
        this.loadRecordMessages(false)
        this.sendMetricComponent('open_messages_modal')
        this.modalRecordMessage = Object.assign({}, recordMessage)
        modal.open()
      },
      openMessages() {
        this.showing = 'messages'
        this.loadRecordMessages()
      },
      hideIt() {
        this.showing = null
      },
      deleteRecordMessage() {
        this.isProcessing = true
        this.store.destroyRecordMessage(this.record.id, this.modalRecordMessage.id).then(() => {
          this.sendMetricComponent('delete_message')
        }).finally(() => {
          this.$emit('reload-callback')
          this.$refs.deleteRecordMessageModal.close()
          this.isProcessing = false

          this.loadRecordMessages()
        })
      },
      createRecordMessage(e){
        let params = new FormData(e.target)

        this.selectedAddressBook.forEach(function (item) {
          let index = item.id.split('-')
          let id = index[0]
          let resource = index[1]

          if(resource === 'Contact')
            params.append('record_message_sent[contact_ids][]', id)
          if(resource === 'User')
            params.append('record_message_sent[user_ids][]', id)
        });

        this.isProcessing = true
        this.store.createRecordMessage(this.record.id, params).then(({}) => {
          this.$toastr.success(this.$t('record_message.message_successfully_sent'))
          this.sendMetricComponent('send_message')
          this.$emit('reload-callback')
          this.loadRecordMessages()
          this.selectedAddressBook = []
          this.recordMessageBody = ''
          e.target.reset()
        }).finally(() => {
          this.isProcessing = false
        })
      },
      closeRecordMessage(){
        this.loadRecordMessages(false)
        if(this.selectedReplyMessage && this.selectedMessage){
          if(this.selectedMessage.replies.length > 0)
            this.showing = this.selectedMessage.id+"-replies"
          else
              this.showing = 'messages'
        }else{
          this.showing = 'messages'
          this.selectedMessage = {}
          this.selectedReplyMessage = {}
        }
        this.updateStatsCounters()
      },
      unreadStats(record_message_id) {
        return this.unreadRepliesCount(record_message_id) + this.unreadMessagesCount(record_message_id)
      },
      unreadRepliesCount(record_message_id) {
        const currentUser = this.currentUser
        return this.repliesStats.filter(item => {
          if(item.record_message_parent_id === record_message_id &&
             item.to_user_id == currentUser.id){
            return item
          }
        }).length
      },
      unreadMessagesCount(record_message_id) {
        const currentUser = this.currentUser
        return this.newMessageStats.filter(item => {
          if(item.record_message_id === record_message_id &&
             item.to_user_id == currentUser.id)
            return item
        }).length
      },
      sentTo(message){
          return this.unique(
                   this.sentMessageToNames(message).flat()
                   .concat(this.sentForwardings(message).flat())
                 ).join(', ')
      },
      replySentTo(reply_message){
        let currentOrg = this.currentOrg
        let sent_messages =  reply_message.sent_messages || []
        return sent_messages.map( (item) => {
          if(item.to_user !== undefined){
            if(item.to_user.organization_id == currentOrg.id)
              return item.to_user.full_name
          }
          if(item.to_contact !== undefined){
            if(item.to_contact.organization_id == currentOrg.id)
              return item.to_contact.name
          }
        }, []).join(', ')
      },
      sentMessageToNames(message){
        let currentOrg = this.currentOrg
        let sent_messages =  message.sent_messages || []
        return sent_messages.map( (item) => {
          if(item.to_user !== undefined){
            if(item.to_user.organization_id == currentOrg.id)
              return item.to_user.full_name
          }
          if(item.to_contact !== undefined){
            if(item.to_contact.organization_id == currentOrg.id)
              return item.to_contact.name
          }
        }, [])
      },
      sentForwardings(message){
        let currentOrg = this.currentOrg
        return message.replies.map( (item) => {

           let sent_messages = item.sent_messages || []
           return sent_messages.map( (sent_message) => {
               let to_user = sent_message.to_user || null
               let to_contact = sent_message.to_contact || null

             if(to_user){
               if(sent_message.to_user.organization_id == currentOrg.id)
                 return to_user.full_name
             }
             if(to_contact){
               if(to_contact.organization_id == currentOrg.id)
                 return to_contact.name
             }
           }, [])
        }, [])
      },
      unique(array){
        return array.filter((v, i, a) => a.indexOf(v) === i)
      },
      openNewRecordMessage(){
        this.showing='newRecordMessage'
        this.selectedAddressBook = []
        this.sendMetricComponent('new_message_form')
      },
      showReplyForm(message, replyMessage){
        this.selectedMessage = message
        this.selectedReplyMessage = replyMessage
        this.recordMessageBody = ''

        this.showing = message.id+'-replyRecordMessage'
        this.sendMetricComponent('reply_form')
      },
      showForwardRecordMessage(message){
        this.selectedMessage = message
        this.recordMessageBody = message.body

        this.showing=message.id+'-forwardRecordMessage'
        this.sendMetricComponent('forward_form')
      },
      showReplies(record_message, show){
        if(show){
          this.selectedMessage = record_message
          this.showing=record_message.id+'-replies'
          if(this.unreadRepliesCount(record_message.id) > 0 || this.recordMessageStats.length > 0)
            this.updateSeenMessages(record_message.id)
          this.sendMetricComponent('collapse_show_replies')
        }else{
          this.selectedMessage = {}
          this.showing = 'messages'
          this.sendMetricComponent('reply_all_form')
        }
      },
      updateSeenMessages(record_message_id){
        this.store.seenRecordMessage(this.record.id, record_message_id).finally(() => {
          this.loadRecordMessages(false)
          this.updateStatsCounters()
          this.sendMetricComponent('seen_message')
        })
      },
      updateStatsCounters(){
        this.statStore.getCommunicationMessagesStats({ 'filters[record_id]': this.record.id, 'filters[to_user_id]': this.currentUser.id })
        this.statStore.getCommunicationMessagesStats({ 'filters[to_user_id]': this.currentUser.id }, true)
      },
      sendMetricComponent(name){
        let metric_name = 'file_communication_record_modal_'+name

        if(this.resource == 'RecordMessageShow')
          metric_name = 'file_communication_show_'+name

        this.store.sendMetrics({name: metric_name, params: this.$route.params, path: this.$route.fullPath})
      }
    }
  }
</script>

<style lang="sass" scoped>
  @import "../../styles/records"

  .contact-image-wrapper
    width: 45px
    height: 45px
    line-height: 45px
    background: #56DAE2
    font-weight: bold
    text-align: center
    overflow: hidden
    display: block
    float: left
    color: #fff
    margin-right: 7px
    img
      vertical-align: top
      min-width: 100%
      min-height: 100%
      width: auto
      height: auto
    div
      border-radius: 50%
    &.org-pic
      background: #eee
      color: #000

</style>
